/**
 * Merge per-region DQX quarantine tables into one grid dataset and support
 * filtering Results/Summary by uploaded region (Table 1, Table 2, …).
 */

export const DQX_REGION_ID_KEY = "__dqxRegionId";
export const DQX_REGION_LABEL_KEY = "__dqxRegionLabel";
export const DQX_REGION_KEY_KEY = "__dqxRegionKey";
export const DQX_SOURCE_ROW_ID_KEY = "__dqxSourceRowId";
export const DQX_JOB_ID_KEY = "__dqxJobId";

export interface BoardDqxRunRef {
  regionId: string | null;
  runId: number;
  jobId: string;
  /** First file-global source_row_index for this mapping run (multi-table offset). */
  sourceRowIndexStart?: number | null;
  totalRowsChecked?: number | null;
}

export interface DqxRegionFilterOption {
  /** Stable filter key (region id or synthetic table key). */
  key: string;
  label: string;
  regionId: string | null;
  jobId: string;
}

export interface QuarantineJobSlice {
  regionId: string | null;
  jobId: string;
  /** 0-based order among board runs (Table N = index + 1). */
  tableIndex: number;
  quarantine_table: Record<string, unknown>[];
  quarantine_summary_table?: Record<string, unknown>[];
}

export interface MergedQuarantineResult {
  table: Record<string, unknown>[];
  summaryTable: Record<string, unknown>[];
  regionOptions: DqxRegionFilterOption[];
  /** Grid row index (0-based) → region filter key. */
  rowIndexToRegionKey: Map<number, string>;
}

/** Filter key for a board run at index (region id when present). */
export function regionFilterKeyForRun(
  regionId: string | null | undefined,
  tableIndex: number
): string {
  const trimmed = regionId?.trim();
  if (trimmed) return trimmed;
  return `__table_${tableIndex}__`;
}

export function buildDqxRegionFilterOptions(
  runs: BoardDqxRunRef[],
  labels?: (string | null | undefined)[]
): DqxRegionFilterOption[] {
  return runs.map((run, tableIndex) => ({
    key: regionFilterKeyForRun(run.regionId, tableIndex),
    label: labels?.[tableIndex]?.trim() || `Table ${tableIndex + 1}`,
    regionId: run.regionId ?? null,
    jobId: run.jobId,
  }));
}

function readRowId(row: Record<string, unknown>): unknown {
  for (const k of [
    "row_id",
    "rowId",
    "source_row_index",
    "sourceRowIndex",
    "ROW_ID",
    "SOURCE_ROW_INDEX",
  ]) {
    if (k in row) return row[k];
  }
  return undefined;
}

/**
 * Concatenate per-job quarantine rows, stamp region metadata, and remap
 * `row_id` to a dense 0-based index so the Results grid has no cross-job collisions.
 */
export function mergeQuarantineJobSlices(
  slices: QuarantineJobSlice[],
  labels?: (string | null | undefined)[]
): MergedQuarantineResult {
  const regionOptions: DqxRegionFilterOption[] = slices.map((slice) => ({
    key: regionFilterKeyForRun(slice.regionId, slice.tableIndex),
    label: labels?.[slice.tableIndex]?.trim() || `Table ${slice.tableIndex + 1}`,
    regionId: slice.regionId ?? null,
    jobId: slice.jobId,
  }));

  const table: Record<string, unknown>[] = [];
  const summaryTable: Record<string, unknown>[] = [];
  const rowIndexToRegionKey = new Map<number, string>();

  for (const slice of slices) {
    const key = regionFilterKeyForRun(slice.regionId, slice.tableIndex);
    const label = labels?.[slice.tableIndex]?.trim() || `Table ${slice.tableIndex + 1}`;

    for (const raw of slice.quarantine_table) {
      const originalId = readRowId(raw);
      const displayIndex = table.length;
      const stamped: Record<string, unknown> = {
        ...raw,
        row_id: displayIndex,
        [DQX_REGION_ID_KEY]: slice.regionId,
        [DQX_REGION_LABEL_KEY]: label,
        [DQX_REGION_KEY_KEY]: key,
        [DQX_SOURCE_ROW_ID_KEY]: originalId,
        [DQX_JOB_ID_KEY]: slice.jobId,
      };
      table.push(stamped);
      rowIndexToRegionKey.set(displayIndex, key);
    }

    if (slice.quarantine_summary_table?.length) {
      for (const s of slice.quarantine_summary_table) {
        summaryTable.push({
          ...s,
          [DQX_REGION_ID_KEY]: slice.regionId,
          [DQX_REGION_LABEL_KEY]: label,
          [DQX_REGION_KEY_KEY]: key,
          [DQX_JOB_ID_KEY]: slice.jobId,
        });
      }
    }
  }

  return { table, summaryTable, regionOptions, rowIndexToRegionKey };
}

/**
 * Row indices (0-based grid) for the selected region filter.
 * `null` / `"all"` → undefined (no region scoping).
 */
export function rowIndicesForRegionFilter(
  rowIndexToRegionKey: Map<number, string> | null | undefined,
  selectedKey: string | null
): number[] | undefined {
  if (!selectedKey || selectedKey === "all") return undefined;
  if (!rowIndexToRegionKey || rowIndexToRegionKey.size === 0) return [];
  const out: number[] = [];
  for (const [idx, key] of rowIndexToRegionKey) {
    if (key === selectedKey) out.push(idx);
  }
  return out.sort((a, b) => a - b);
}

/** Intersect two optional row-index lists. `undefined` means “no constraint”. */
export function intersectRowIndexFilters(
  a: number[] | undefined,
  b: number[] | undefined
): number[] | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  const setB = new Set(b);
  return a.filter((i) => setB.has(i)).sort((x, y) => x - y);
}

/**
 * Quarantine rows belonging to the selected region (for Summary recomputation).
 * When All, returns the full table.
 */
export function filterQuarantineTableByRegion(
  table: Record<string, unknown>[],
  selectedKey: string | null
): Record<string, unknown>[] {
  if (!selectedKey || selectedKey === "all") return table;
  return table.filter((row) => row[DQX_REGION_KEY_KEY] === selectedKey);
}
